import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { USBConnectionProvider } from "@/contexts/USBConnectionContext";
import LandingPage from "@/pages/LandingPage";
import UserManualPage from "@/pages/UserManualPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import SystemsPage from "@/pages/SystemsPage";
import ScanPage from "@/pages/ScanPage";
import EventsPage from "@/pages/EventsPage";
import FrequenciesPage from "@/pages/FrequenciesPage";
import ReportsPage from "@/pages/ReportsPage";
import ChileFrequenciesPage from "@/pages/ChileFrequenciesPage";
import HardwarePage from "@/pages/HardwarePage";
import OfflineSyncPage from "@/pages/OfflineSyncPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import FlowResultPage from "@/pages/FlowResultPage";
import AdminPage from "@/pages/AdminPage";
import ProfilePage from "@/pages/ProfilePage";
import TeamPage from "@/pages/TeamPage";
import InvitationPage from "@/pages/InvitationPage";
import VenuesPage from "@/pages/VenuesPage";
import SupportPage from "@/pages/SupportPage";
import IntermodulationPage from "@/pages/IntermodulationPage";
import TemplatesPage from "@/pages/TemplatesPage";
import CoverageMapPage from "@/pages/CoverageMapPage";
import ChangelogPage from "@/pages/ChangelogPage";
import ReleaseNotesModal from "@/components/updates/ReleaseNotesModal";
import { useNotifications } from "@/hooks/useNotifications";

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  
  // Only initialize notifications when logged in - disabled for now to prevent refresh issues
  // const { isConnected: wsConnected, notifications } = useNotifications(token);
  const wsConnected = false;
  const notifications = [];

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
      
      // Sync user data with backend (in case plan was changed by admin)
      const syncUser = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            localStorage.setItem("user", JSON.stringify(userData));
          }
        } catch (error) {
          console.error("Failed to sync user data:", error);
        }
      };
      syncUser();
    }
  }, [token]);

  const handleLogin = (userData, accessToken) => {
    setUser(userData);
    setToken(accessToken);
    localStorage.setItem("token", accessToken);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  };

  const ProtectedRoute = ({ children }) => {
    if (!token) {
      return <Navigate to="/login" replace />;
    }
    return children;
  };

  // Redirect from old dashboard route
  const DashboardRedirect = () => {
    if (token) {
      return <Navigate to="/dashboard" replace />;
    }
    return <LandingPage onLogin={handleLogin} />;
  };

  return (
    <div className="app-container">
      <Toaster position="top-right" richColors />
      <USBConnectionProvider>
      <BrowserRouter>
        <Routes>
          {/* Root shows Landing Page */}
          <Route path="/" element={<LandingPage onLogin={handleLogin} />} />
          
          {/* Landing Page also accessible at /landing */}
          <Route path="/landing" element={<LandingPage onLogin={handleLogin} />} />
          
          {/* User Manual - Public */}
          <Route path="/manual" element={<UserManualPage />} />
          
          {/* Terms & Privacy - Public */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          
          {/* Login Page */}
          <Route 
            path="/login" 
            element={
              token ? <Navigate to="/dashboard" replace /> : <LoginPage onLogin={handleLogin} />
            } 
          />
          
          {/* Password Recovery Pages - Public */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Protected Dashboard */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage user={user} onLogout={handleLogout} wsConnected={wsConnected} notifications={notifications} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/systems"
            element={
              <ProtectedRoute>
                <SystemsPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/scan"
            element={
              <ProtectedRoute>
                <ScanPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/events"
            element={
              <ProtectedRoute>
                <EventsPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates"
            element={
              <ProtectedRoute>
                <TemplatesPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coverage-map"
            element={
              <ProtectedRoute>
                <CoverageMapPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/frequencies"
            element={
              <ProtectedRoute>
                <FrequenciesPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chile-frequencies"
            element={
              <ProtectedRoute>
                <ChileFrequenciesPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/venues"
            element={
              <ProtectedRoute>
                <VenuesPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hardware"
            element={
              <ProtectedRoute>
                <HardwarePage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/offline"
            element={
              <ProtectedRoute>
                <OfflineSyncPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <ReportsPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <SubscriptionPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription/flow-result"
            element={
              <ProtectedRoute>
                <FlowResultPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage user={user} setUser={setUser} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team"
            element={
              <ProtectedRoute>
                <TeamPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <SupportPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/intermodulation"
            element={
              <ProtectedRoute>
                <IntermodulationPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/changelog"
            element={
              <ProtectedRoute>
                <ChangelogPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          {/* Invitation Page - Semi-public */}
          <Route
            path="/invitation"
            element={<InvitationPage user={user} />}
          />
        </Routes>
        {/* Release Notes Modal - shows after login when new version is available */}
        {user && <ReleaseNotesModal onExplore={() => window.location.href = '/hardware'} />}
      </BrowserRouter>
    </USBConnectionProvider>
    </div>
  );
}

export default App;
